<template>
  <div
    ref="containerButton"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
    class="canvas-button-container"
    :class="{ 'has-image': imageUrl }"
  >
    <canvas ref="canvas"></canvas>

    <custom-button
      v-if="!imageUrl"
      @click.stop="emits('search')"
      class="custom-sizes-button"
      iconPos="right"
      icon="pi pi-search"
      :label="t('home_page.search')"
      :disabled="!imageSearch.croppedImageLink && disableSearch"
      rounded
      size="large"
    />
    <div
      v-if="!imageUrl"
      class="wrapper-for-container-custom-buttons"
      @click="handleClick"
    >
      <span class="add-photo-text">{{ t("home_page.add_photo") }}</span>
      <span class="plus-icon"> + </span>
    </div>

    <div v-else class="wrapper-for-container-custom-buttons" @click.stop="editPhotoFlow">
      <Button
        @click.stop="deleteImageFromSearch"
        icon="pi pi-trash"
        severity="warn"
        class="blur"
        rounded
      />

      <custom-button
        icon="pi pi-search"
        label="Search"
        icon-pos="right"
        severity="primary"
        @click.stop="emits('search')"
        rounded
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onBeforeUnmount, computed } from "vue";
import { useSharedStateForComponents, useImageSearch } from "@/features/search/store";
import Button from "primevue/button";
import { useDevice } from "@/composables/useDevice";
import CustomButton from "@/components/UI/CustomButtom/CustomButton.vue";
import { useI18n } from "vue-i18n";
import { debounce } from "@/helpers/debounce";

const props = defineProps({
  imageUrl: {
    type: String,
    default: "",
  },
  animationDuration: {
    type: Number,
    default: 300,
  },
  disableSearch: {
    type: Boolean,
    default: true,
  },
});

const emits = defineEmits(["search"]);

const imageSearch = useImageSearch();
const { isDevice } = useDevice();
const { t } = useI18n();

const deviceType = computed(() => isDevice.value);
const { setStep1ForImageFlow, setStep2ForImageFlow } = useSharedStateForComponents();

const canvas = ref<HTMLCanvasElement | null>(null);
const containerButton = ref<HTMLElement | null>(null);
const animationProgress = ref(0);
const currentImage = ref<HTMLImageElement | null>(null);
const animationFrame = ref<number | null>(null);
const isHovered = ref(false);

// Debounced resize handler
const debouncedResize = debounce(() => {
  if (canvas.value && containerButton.value) {
    canvas.value.width = containerButton.value.clientWidth;
    canvas.value.height = containerButton.value.clientHeight;
    drawButton();
  }
}, 100);

function deleteImageFromSearch() {
  imageSearch.currentImageLink = "";
  imageSearch.imageBase64Data = null;
  imageSearch.imageFile = undefined;
  imageSearch.croppedImageLink = null;
}

function editPhotoFlow() {
  setStep2ForImageFlow();
  imageSearch.isImageSearchModalOpen = true;
}

function startAddingPhotoFlow() {
  setStep1ForImageFlow();
  imageSearch.isImageSearchModalOpen = true;
}

function drawButton(withAnimation = false) {
  if (!canvas.value) return;

  const ctx = canvas.value.getContext("2d");
  if (!ctx) return;

  const width = canvas.value.clientWidth;
  const height = canvas.value.clientHeight;

  ctx.clearRect(0, 0, width, height);

  const buttonWidth = width - 140;
  const buttonHeight = height;
  const offsetX = 0;
  const offsetY = 0;
  const radius = 30;

  ctx.beginPath();

  if (currentImage.value) {
    ctx.moveTo(offsetX + radius, offsetY);
    ctx.lineTo(offsetX + width - radius, offsetY);
    ctx.arcTo(offsetX + width, offsetY, offsetX + width, offsetY + radius, radius);
    ctx.lineTo(offsetX + width, offsetY + buttonHeight - radius);
    ctx.arcTo(
      offsetX + width,
      offsetY + buttonHeight,
      offsetX + width - radius,
      offsetY + buttonHeight,
      radius
    );
    ctx.lineTo(offsetX + radius, offsetY + buttonHeight);
    ctx.arcTo(
      offsetX,
      offsetY + buttonHeight,
      offsetX,
      offsetY + buttonHeight - radius,
      radius
    );
    ctx.lineTo(offsetX, offsetY + radius);
    ctx.arcTo(offsetX, offsetY, offsetX + radius, offsetY, radius);
  } else {
    const shelfWidth = 140;
    const shelfHeight = buttonHeight * 0.5;

    ctx.moveTo(offsetX + radius, offsetY);
    ctx.lineTo(offsetX + buttonWidth - shelfWidth, offsetY);
    ctx.arcTo(
      offsetX + buttonWidth,
      offsetY,
      offsetX + buttonWidth,
      offsetY + radius,
      radius
    );
    ctx.lineTo(offsetX + buttonWidth, offsetY + shelfHeight - radius);
    ctx.arcTo(
      offsetX + buttonWidth,
      offsetY + shelfHeight,
      offsetX + buttonWidth + radius,
      offsetY + shelfHeight,
      radius
    );
    ctx.lineTo(offsetX + buttonWidth + shelfWidth - radius, offsetY + shelfHeight);
    ctx.arcTo(
      offsetX + buttonWidth + shelfWidth,
      offsetY + shelfHeight,
      offsetX + buttonWidth + shelfWidth,
      offsetY + shelfHeight + radius,
      radius
    );
    ctx.lineTo(offsetX + buttonWidth + shelfWidth, offsetY + buttonHeight - radius);
    ctx.arcTo(
      offsetX + buttonWidth + shelfWidth,
      offsetY + buttonHeight,
      offsetX + buttonWidth + shelfWidth - radius,
      offsetY + buttonHeight,
      radius
    );
    ctx.lineTo(offsetX + radius, offsetY + buttonHeight);
    ctx.arcTo(
      offsetX,
      offsetY + buttonHeight,
      offsetX,
      offsetY + buttonHeight - radius,
      radius
    );
    ctx.lineTo(offsetX, offsetY + radius);
    ctx.arcTo(offsetX, offsetY, offsetX + radius, offsetY, radius);
  }

  ctx.closePath();

  if (currentImage.value) {
    ctx.save();
    ctx.clip();

    const targetWidth = width;
    const scaleRatio = targetWidth / currentImage.value.width;
    const fullHeight = currentImage.value.height * scaleRatio;

    const animatedHeight = withAnimation
      ? fullHeight * animationProgress.value
      : fullHeight;
    const yOffset = (buttonHeight - animatedHeight) / 2;
    const animatedWidth = withAnimation
      ? targetWidth * animationProgress.value
      : targetWidth;
    const xOffset = (targetWidth - animatedWidth) / 2;

    ctx.drawImage(currentImage.value, xOffset, yOffset, animatedWidth, animatedHeight);
    ctx.restore();
  } else {
    drawFallbackButton(ctx, width, height);
  }

  ctx.strokeStyle = "rgba(0, 0, 0, 0)";
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawFallbackButton(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
) {
  const centerX = width / 1.8;
  const centerY = height / 2;

  const gradient = ctx.createRadialGradient(
    centerX,
    centerY,
    40,
    centerX,
    centerY,
    height * 0.9
  );
  gradient.addColorStop(
    0,
    isHovered.value ? "rgba(186, 174, 206, 0.5)" : "rgba(186, 174, 206, 0.3)"
  );
  gradient.addColorStop(
    1,
    isHovered.value ? "rgba(110, 133, 203, 0.5)" : "rgba(110, 133, 203, 0.3)"
  );

  ctx.fillStyle = gradient;
  ctx.fill();
}

function isInShelfArea(x: number, y: number, width: number, height: number): boolean {
  const buttonWidth = width - 140;
  const shelfWidth = 140;
  const shelfHeight = height * 0.5;

  // Main rectangular area
  if (x <= buttonWidth && y <= height && y >= 0) {
    return true;
  }

  // Shelf area
  if (x > buttonWidth && x <= width && y >= shelfHeight && y <= height) {
    return true;
  }

  // Rounded connection part
  const radius = 30;
  const shelfStartX = buttonWidth;
  const shelfStartY = shelfHeight;

  if (
    x > shelfStartX &&
    x < shelfStartX + radius &&
    y > shelfStartY - radius &&
    y < shelfStartY + radius
  ) {
    const dx = x - (shelfStartX + radius);
    const dy = y - shelfStartY;
    return dx * dx + dy * dy <= radius * radius;
  }

  return false;
}

function handleMouseMove(event: MouseEvent) {
  if (currentImage.value || !canvas.value) return;

  const rect = canvas.value.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  const width = canvas.value.clientWidth;
  const height = canvas.value.clientHeight;

  isHovered.value = isInShelfArea(x, y, width, height);
  drawButton();
}

function handleMouseLeave() {
  isHovered.value = false;
  drawButton();
}

async function loadImage(url: string): Promise<HTMLImageElement | null> {
  try {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = url;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    return img;
  } catch (error) {
    console.error("Error loading image:", error);
    return null;
  }
}

function startAnimation() {
  if (animationFrame.value) {
    cancelAnimationFrame(animationFrame.value);
  }
  animationProgress.value = 0;

  const startTime = performance.now();
  const duration = props.animationDuration;

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    animationProgress.value = Math.min(elapsed / duration, 1);
    drawButton(true);

    if (animationProgress.value < 1) {
      animationFrame.value = requestAnimationFrame(animate);
    }
  };

  animationFrame.value = requestAnimationFrame(animate);
}

const handleClick = () => {
  startAddingPhotoFlow();
};

watch(
  () => imageSearch.croppedImageLink,
  async (newUrl) => {
    if (props.imageUrl) {
      currentImage.value = await loadImage(props.imageUrl);
      if (currentImage.value) {
        setTimeout(() => {
          startAnimation();
        }, 200);
      }
    } else {
      currentImage.value = null;
      drawButton();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (canvas.value && containerButton.value) {
    canvas.value.width = containerButton.value.clientWidth;
    canvas.value.height = containerButton.value.clientHeight;
    drawButton();
  }

  // Add window resize listener with debounce
  window.addEventListener("resize", debouncedResize);
});

onBeforeUnmount(() => {
  if (animationFrame.value) {
    cancelAnimationFrame(animationFrame.value);
  }

  // Remove window resize listener
  window.removeEventListener("resize", debouncedResize);
});
</script>

<style>
.canvas-button-container {
  width: 100%;
  height: 140px;
  position: relative;
  cursor: pointer;
}

.canvas-button-container:not(.has-image) canvas:hover {
  cursor: pointer;
}

canvas {
  width: 100%;
  height: 100%;
}

.wrapper-for-container-custom-buttons {
  display: flex;
  top: 0;
  justify-content: space-between;
  align-items: flex-end;
  width: 98%;
  height: 100%;
  position: absolute;
  z-index: 1;
  padding: 0 2px 10px 10px;
}

.add-photo-text {
  display: flex;
  justify-content: center;
  align-items: center;
  width: min-content;
  font-size: 48px;
  line-height: 110%;
  letter-spacing: -2px;
  padding: 0 0 10px 10px;
  font-family: Gilroy, sans-serif;
}

.plus-icon {
  height: 55%;
  font-size: 52px;
  color: var(--primary-blue-100);
}

.custom-sizes-button {
  position: absolute;
  top: 2px;
  right: 4px;
  width: 125px;
  height: calc(50% - 11px);
  max-width: 125px;
  max-height: 60px;
  z-index: 4;
}
</style>
